// chatStore.ts -- Zustand registry for N independent chat windows.
//
// Architectural shift from the old single-session useAgent context:
// each open chat is its own window with its own session, messages,
// recipient, position, etc. Multiple windows can target the same
// recipient, the same session, or be empty drafts -- they don't
// coordinate beyond sharing project context.
//
// Window lifecycle:
//   1. Open -- created via openWindow({recipient, sessionId?}).
//      Generates a windowId (uuid) and inserts into the registry.
//      No server round-trip yet unless sessionId is supplied (resume).
//   2. Draft -- window exists, recipient is set, no messages, no
//      sessionId. User can change recipient (rare) or close.
//   3. First send -- send() creates the session server-side via
//      POST /chat/sessions, stamps sessionId on the window, then
//      streams the message via SSE. Subsequent sends reuse the
//      session.
//   4. Resume -- a window opened with a sessionId loads its history
//      via loadHistory before any user input. Same final state shape
//      as a window that's sent and received over time.
//   5. Close -- removes from registry. Server data persists.
//
// Per-window state lives entirely in this store. Components subscribe
// to their slice via useChatStore(s => s.windows[id]) -- selector
// memoization means a window's render only re-runs when ITS slice
// changes, not when any other window updates.
//
// Side-effect handlers (refresh-folder, navigate, toast, etc) are
// project-global; the store holds them in a single registry that
// non-React callers (poll loops, future workers) can call. ProjectShell
// registers handlers on mount via setSideEffectHandlers.
import { create } from 'zustand';
import { api } from '../utils/api.js';
import { recipientForWire, recipientKey } from '../utils/recipient.js';
import { applyEvent, streamAgent, } from '../utils/agentStream.js';
// ---------------------------------------------------------------------------
// Defaults + constants
// ---------------------------------------------------------------------------
const DEFAULT_W = 480;
const DEFAULT_H = 640;
const CASCADE_OFFSET = 32;
const POSE_KEY_PREFIX = 'fdn:chat-window:';
/**
 * localStorage key for the persisted registry. Survives refresh; the
 * store hydrates from this on first creation. Drafts (no sessionId)
 * are NOT persisted -- they're tab-local by design. See workbench
 * C.5.6: real-session windows survive refresh; drafts are ephemeral.
 */
const REGISTRY_KEY = 'fdn:chat-registry-v1';
function loadRegistry() {
    try {
        const raw = localStorage.getItem(REGISTRY_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (parsed.version !== 1)
            return null;
        if (!Array.isArray(parsed.windowOrder))
            return null;
        if (!parsed.windows || typeof parsed.windows !== 'object')
            return null;
        return {
            version: 1,
            windowOrder: parsed.windowOrder.filter((id) => typeof id === 'string'),
            windows: parsed.windows,
        };
    }
    catch {
        return null;
    }
}
/**
 * Serialize the current registry to localStorage, skipping drafts.
 * Called from any action that mutates the persisted shape: openWindow
 * with sessionId, ensureSession's sessionId-write, closeWindow,
 * minimizeWindow, focusWindow, setWindowPose. Cheap (small JSON).
 */
function persistRegistry(windows, windowOrder) {
    try {
        const persisted = {
            version: 1,
            windowOrder: [],
            windows: {},
        };
        for (const id of windowOrder) {
            const w = windows[id];
            // Drafts (no sessionId) are NOT persisted. Skip them entirely
            // including from windowOrder so a refresh starts fresh on those.
            if (!w || !w.sessionId)
                continue;
            persisted.windowOrder.push(id);
            persisted.windows[id] = {
                windowId: w.windowId,
                recipient: w.recipient,
                sessionId: w.sessionId,
                sessionTitle: w.sessionTitle,
                pose: w.pose,
            };
        }
        localStorage.setItem(REGISTRY_KEY, JSON.stringify(persisted));
    }
    catch {
        // localStorage full / disabled -- fail silently. Persistence is
        // a nicety, not a correctness requirement.
    }
}
function loadPose(windowId) {
    try {
        const raw = localStorage.getItem(`${POSE_KEY_PREFIX}${windowId}`);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.x === 'number'
            && typeof parsed.y === 'number'
            && typeof parsed.w === 'number'
            && typeof parsed.h === 'number') {
            return {
                x: parsed.x,
                y: parsed.y,
                w: parsed.w,
                h: parsed.h,
                minimized: Boolean(parsed.minimized),
                focusedAt: typeof parsed.focusedAt === 'number' ? parsed.focusedAt : Date.now(),
            };
        }
    }
    catch {
        // ignore
    }
    return null;
}
function savePose(windowId, pose) {
    try {
        localStorage.setItem(`${POSE_KEY_PREFIX}${windowId}`, JSON.stringify(pose));
    }
    catch {
        // ignore
    }
}
function clearPose(windowId) {
    try {
        localStorage.removeItem(`${POSE_KEY_PREFIX}${windowId}`);
    }
    catch {
        // ignore
    }
}
function defaultPose(existingCount) {
    const baseX = Math.max(80, (typeof window !== 'undefined' ? window.innerWidth : 1280) - DEFAULT_W - 64);
    const baseY = 96;
    const cascade = (existingCount % 8) * CASCADE_OFFSET;
    return {
        x: baseX - cascade,
        y: baseY + cascade,
        w: DEFAULT_W,
        h: DEFAULT_H,
        minimized: false,
        focusedAt: Date.now(),
    };
}
function newWindowId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `w-${crypto.randomUUID()}`;
    }
    return `w-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}
function isHumanDM(r) {
    return r.kind === 'user';
}
// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
export const useChatStore = create((set, get) => {
    // Side-effect handler registry. Stored OUTSIDE the Zustand state so
    // re-registrations don't trigger window re-renders. Components call
    // setSideEffectHandlers to layer in their own implementations.
    const sideEffectHandlers = {};
    // Per-window optimistic-id monotonic counter. Two sends fired in
    // the same millisecond (rapid Enter or paste-into-many) would
    // otherwise produce identical `u-<Date.now()>` ids, which then
    // collide when the SSE id-swap on user-saved tries to find the
    // optimistic message. Tagging each id with a per-window sequence
    // (`u-<ms>-<seq>`) guarantees uniqueness regardless of clock
    // granularity. Per-window keying preserves ordering AND ensures
    // two parallel windows don't share counter state.
    const sendSeqByWindow = new Map();
    const nextSendSeq = (windowId) => {
        const cur = sendSeqByWindow.get(windowId) ?? 0;
        const next = cur + 1;
        sendSeqByWindow.set(windowId, next);
        return next;
    };
    // Per-window AbortController for in-flight LLM streams. Stored
    // OUTSIDE Zustand state because AbortController isn't serializable
    // (and we don't want Zustand re-rendering windows when the
    // controller is swapped). The map is keyed by windowId; each
    // entry lives for the duration of one assistant stream.
    //
    // When the user clicks Stop, we look up the controller, call
    // abort(), and the streamAgent's fetch unwinds with an AbortError
    // that the send() catch block treats as a clean termination.
    const inFlightControllers = new Map();
    function dispatchSideEffect(eff) {
        const h = sideEffectHandlers;
        switch (eff.type) {
            case 'refresh-folder':
                h.refreshFolder?.(eff.folderId);
                break;
            case 'refresh-trash':
                h.refreshTrash?.();
                break;
            case 'navigate-folder':
                h.navigateFolder?.(eff.folderId);
                break;
            case 'select-item':
                h.selectItem?.(eff.itemId);
                break;
            case 'open-preview':
                h.openPreview?.(eff.itemId);
                break;
            case 'close-preview':
                h.closePreview?.();
                break;
            case 'toast':
                h.toast?.({ level: eff.level, message: eff.message });
                break;
        }
    }
    /** Apply a per-window patch to the store. Returns void. */
    function patchWindow(windowId, patch) {
        set((s) => {
            const w = s.windows[windowId];
            if (!w)
                return s;
            const nextWindows = { ...s.windows, [windowId]: { ...w, ...patch } };
            // If this patch touches anything that's serialized to the
            // registry (sessionId, sessionTitle, recipient, pose), the
            // localStorage copy needs to refresh too. Cheap; small JSON.
            const patchTouchesPersisted = 'sessionId' in patch
                || 'sessionTitle' in patch
                || 'recipient' in patch
                || 'pose' in patch;
            if (patchTouchesPersisted) {
                persistRegistry(nextWindows, s.windowOrder);
            }
            return { windows: nextWindows };
        });
    }
    /**
     * Per-window setMessages adapter passed to applyEvent. Reads the
     * current messages slice for the window, runs fn, writes back.
     */
    function setMessagesFor(windowId) {
        return (fn) => {
            set((s) => {
                const w = s.windows[windowId];
                if (!w)
                    return s;
                return {
                    windows: {
                        ...s.windows,
                        [windowId]: { ...w, messages: fn(w.messages) },
                    },
                };
            });
        };
    }
    return {
        projectId: null,
        project: null,
        role: null,
        windows: {},
        windowOrder: [],
        pendingHint: null,
        projectCurrentFolder: null,
        setProjectContext: (ctx) => {
            set({ projectId: ctx.projectId, project: ctx.project, role: ctx.role });
        },
        hydrateFromStorage: () => {
            // Idempotent: if the registry already has any entries, skip.
            // This matters because the AgentProvider effect can fire more
            // than once (StrictMode in dev, prop changes, etc).
            if (get().windowOrder.length > 0)
                return;
            const persisted = loadRegistry();
            if (!persisted)
                return;
            const projFolder = get().projectCurrentFolder;
            const restoredWindows = {};
            for (const id of persisted.windowOrder) {
                const p = persisted.windows[id];
                if (!p)
                    continue;
                restoredWindows[id] = {
                    windowId: p.windowId,
                    recipient: p.recipient,
                    sessionId: p.sessionId,
                    sessionTitle: p.sessionTitle,
                    messages: [],
                    sending: false,
                    hint: null,
                    nextMessageContentType: 'text',
                    currentFolderId: projFolder?.id ?? null,
                    currentFolderMeta: projFolder,
                    lastSentFolderId: null,
                    pose: p.pose,
                    inlineUnread: 0,
                };
            }
            set({
                windows: restoredWindows,
                windowOrder: [...persisted.windowOrder],
            });
            // Fire loadHistory for each rehydrated window so messages catch
            // up with server-side state. Best-effort; errors are caught
            // inside loadHistory.
            for (const id of persisted.windowOrder) {
                void get().loadHistory(id);
            }
        },
        setPendingHint: (hint) => {
            set({ pendingHint: hint });
        },
        setProjectCurrentFolder: (folder) => {
            set({ projectCurrentFolder: folder });
        },
        setSideEffectHandlers: (h) => {
            Object.assign(sideEffectHandlers, h);
        },
        triggerOpenConversationsPanel: () => {
            sideEffectHandlers.openConversationsPanel?.();
        },
        // ---- Lifecycle ----
        openWindow: ({ recipient, sessionId }) => {
            const windowId = newWindowId();
            const existing = get().windowOrder.length;
            const pose = loadPose(windowId) ?? defaultPose(existing);
            const projFolder = get().projectCurrentFolder;
            const win = {
                windowId,
                recipient,
                sessionId: sessionId ?? null,
                sessionTitle: null,
                messages: [],
                sending: false,
                hint: null,
                nextMessageContentType: 'text',
                currentFolderId: projFolder?.id ?? null,
                currentFolderMeta: projFolder,
                lastSentFolderId: null,
                pose,
                inlineUnread: 0,
            };
            set((s) => {
                const nextWindows = { ...s.windows, [windowId]: win };
                const nextOrder = [...s.windowOrder, windowId];
                // Only resume-windows (with sessionId) are persisted; drafts
                // are skipped by persistRegistry, so this is safe to call
                // unconditionally.
                if (sessionId)
                    persistRegistry(nextWindows, nextOrder);
                return {
                    windows: nextWindows,
                    windowOrder: nextOrder,
                };
            });
            if (sessionId) {
                // Resume: fire-and-forget the history load.
                void get().loadHistory(windowId);
            }
            return windowId;
        },
        openOrFocusRecipientWindow: async (recipient, projectId) => {
            // Step 1: is a window with this recipient already open? Match on
            // recipientKey (the canonical join key) so the same person is
            // never duplicated, even if a UI field drifted between writes.
            const targetKey = recipientKey(recipient);
            const { windows, windowOrder } = get();
            for (const id of windowOrder) {
                const w = windows[id];
                if (!w)
                    continue;
                if (recipientKey(w.recipient) === targetKey) {
                    // Found it. Un-minimize and raise to top (focusWindow handles
                    // both the focusedAt bump + persistRegistry).
                    if (w.pose.minimized)
                        get().minimizeWindow(id, false);
                    else
                        get().focusWindow(id);
                    return id;
                }
            }
            // Step 2: no open window. Look up sessions for this recipient.
            // The /sessions/by-recipient/:key endpoint returns owner-or-
            // recipient sessions (per server route). Best-effort: if the
            // call fails, we fall back to opening a fresh draft.
            try {
                const r = await api.get(`/p/${projectId}/chat/sessions/by-recipient/${encodeURIComponent(targetKey)}`);
                const sessions = r.sessions ?? [];
                if (sessions.length === 1) {
                    // Exactly one session -- resume it.
                    const sole = sessions[0];
                    return get().openWindow({ recipient, sessionId: sole.sessionId });
                }
                // Zero or 2+ sessions: open a fresh draft. The user can pick a
                // specific past session via the history (📜) button if they
                // want one of the others.
                return get().openWindow({ recipient });
            }
            catch {
                // Network / auth failure: fall through to draft.
                return get().openWindow({ recipient });
            }
        },
        closeWindow: (windowId) => {
            clearPose(windowId);
            set((s) => {
                const { [windowId]: _, ...rest } = s.windows;
                void _;
                const nextOrder = s.windowOrder.filter((id) => id !== windowId);
                persistRegistry(rest, nextOrder);
                return {
                    windows: rest,
                    windowOrder: nextOrder,
                };
            });
        },
        minimizeWindow: (windowId, minimized) => {
            const w = get().windows[windowId];
            if (!w)
                return;
            const nextPose = { ...w.pose, minimized };
            if (!minimized) {
                // Restoring clears any inline-unread accumulated while
                // minimized + bumps focusedAt so the restored window stacks
                // on top.
                nextPose.focusedAt = Date.now();
            }
            savePose(windowId, nextPose);
            patchWindow(windowId, {
                pose: nextPose,
                inlineUnread: minimized ? w.inlineUnread : 0,
            });
        },
        focusWindow: (windowId) => {
            const w = get().windows[windowId];
            if (!w)
                return;
            const nextPose = { ...w.pose, focusedAt: Date.now() };
            savePose(windowId, nextPose);
            patchWindow(windowId, { pose: nextPose });
        },
        setWindowPose: (windowId, patch) => {
            const w = get().windows[windowId];
            if (!w)
                return;
            const nextPose = { ...w.pose, ...patch };
            savePose(windowId, nextPose);
            patchWindow(windowId, { pose: nextPose });
        },
        setHint: (windowId, hint) => {
            patchWindow(windowId, { hint });
        },
        setNextMessageContentType: (windowId, contentType) => {
            patchWindow(windowId, { nextMessageContentType: contentType });
        },
        setCurrentFolder: (windowId, folderId, name, path) => {
            patchWindow(windowId, {
                currentFolderId: folderId,
                currentFolderMeta: { id: folderId, name, path },
            });
        },
        setRecipient: (windowId, recipient) => {
            const w = get().windows[windowId];
            if (!w || w.sessionId)
                return; // recipient is locked once session exists
            patchWindow(windowId, { recipient });
        },
        // ---- Persistence + streaming ----
        /**
         * ensureSession + streamAgent for THIS window. Creates the server
         * session on first call (POST /chat/sessions), then streams the
         * message via SSE. Subsequent sends reuse the established session.
         */
        send: async (windowId, text, viewerUserId) => {
            const trimmed = text.trim();
            if (!trimmed)
                return;
            const projectId = get().projectId;
            if (!projectId) {
                throw new Error('chatStore.send: project context not set');
            }
            const w = get().windows[windowId];
            if (!w)
                return;
            const humanDM = isHumanDM(w.recipient);
            // The sending lock only blocks LLM-assistant sessions where
            // the assistant is mid-stream. Human-only DMs send in parallel
            // (WhatsApp / iMessage / Slack DMs all allow this -- the user
            // can fire many messages back-to-back without waiting on the
            // previous one to ack).
            if (!humanDM && w.sending)
                return;
            // Hint is sent SEPARATELY to the BFF (not concatenated into
            // content) so the persisted message droplet stores only the
            // user's typed text. The LLM still sees hint+text via the BFF
            // server-side composition. Replays no longer leak the hint.
            const hiddenHint = w.hint?.hidden ?? '';
            // Per-message content-type chip (default 'text', set by the
            // composer's content-type chip menu). Captured BEFORE the
            // patchWindow that resets it -- otherwise we'd see the reset
            // value mid-send.
            const sendContentType = w.nextMessageContentType ?? 'text';
            // Capture parentMessageId BEFORE the optimistic-push patchWindow
            // so it reflects the previous server-confirmed message (or the
            // previous optimistic one for parallel rapid-fire sends). The
            // server uses this as the new message's parentMessageId rather
            // than re-reading sessionDroplet.lastMessageId, which would race
            // between concurrent POSTs and produce duplicate parent links.
            //
            // Walks the messages array in reverse looking for the most-recent
            // message id we can use as a parent. Optimistic ids (u-*, a-*)
            // are valid parents here -- the server will see them in the
            // POST body and accept them; the next id-swap will not re-link
            // because parent is recorded at write time, not retroactively.
            // For the FIRST message in a fresh session, parentMessageId is
            // null (server falls back to sessionDroplet.lastMessageId which
            // is also null for an empty session).
            let parentMessageId = null;
            for (let i = w.messages.length - 1; i >= 0; i--) {
                const m = w.messages[i];
                if (m.id) {
                    parentMessageId = m.id;
                    break;
                }
            }
            // Push optimistic user message (always) + assistant placeholder
            // (only for non-DM sessions; human-DMs short-circuit on the BFF
            // and never produce assistant frames).
            //
            // The optimistic-id pattern (industry standard, used by Slack,
            // Teams, Discord, iMessage):
            //   1. Assign a TEMPORARY client-side id (`u-<ts>`) so the UI
            //      can render the message immediately while the server
            //      round-trip is in flight.
            //   2. POST to server.
            //   3. Server confirms via the `user-saved` SSE event carrying
            //      the REAL server-side messageId (UUIDv7).
            //   4. SWAP: replace the temporary id with the real id in the
            //      local message store. (Step 4 was MISSING -- causing the
            //      `u-<ts>` id to leak into asc-poll cursors which the
            //      server cannot resolve.)
            // From step 4 onward every consumer (cursor tracking, scroll
            // position, retry-on-failure detection) operates on the real
            // server id. The temporary id only exists between steps 1-3
            // (typically < 200ms) and never appears on any wire request
            // after the swap.
            const seq = nextSendSeq(windowId);
            const sendTs = Date.now();
            const optimisticUserId = `u-${sendTs}-${seq}`;
            // Stamp the viewer's userId on the optimistic message so
            // pollNew's tuple-match dedup (authorId, role, content) can
            // recognize the polled-in real message as the same row. Without
            // authorId the optimistic message's tuple key carries `undefined`
            // while the polled message carries the real user uuid -- the
            // tuple match fails, the polled message gets appended, and the
            // window renders TWO copies until the user closes + reopens.
            // The duplicate stays in the local UIMessage list because
            // nothing later reconciles them; only a fresh loadHistory
            // (on window reopen) clobbers the local state with the
            // canonical server-side history.
            const userMsg = {
                id: optimisticUserId,
                role: 'user',
                content: trimmed,
                thinking: [],
                createdAt: new Date().toISOString(),
                state: 'complete',
                ...(viewerUserId ? { authorId: viewerUserId } : {}),
            };
            // Assistant placeholder: authorId stays unset. The polled
            // assistant message carries an authorId of the COWORKER
            // (or 'fdn' for default-FDN sessions), not the viewer; the
            // tuple match for assistant messages is content-driven and
            // the streaming placeholder has empty content while it's
            // accumulating, so the polled assistant droplet (with full
            // text content) never collides with an empty placeholder.
            // The SSE `assistant-saved` swap handles the post-stream
            // id swap directly.
            const assistantMsg = {
                id: `a-${sendTs}-${seq}`,
                role: 'assistant',
                content: '',
                thinking: [],
                createdAt: new Date().toISOString(),
                state: 'streaming',
            };
            // The `sending` lock only applies to LLM-assistant turns. For
            // human-only DMs there's no streaming reply to wait for, no
            // model context to preserve, no architectural reason to lock
            // the input. The user can fire many messages back-to-back the
            // way they do in WhatsApp / iMessage / Slack DMs.
            //
            // Each POST is fully parallel. Server-side parentMessageId
            // ordering is preserved because we send parentMessageId
            // explicitly from the request body (computed from the locally-
            // tracked previousMessageId at this exact moment), not derived
            // from a session-droplet lookup that would race between
            // concurrent POSTs.
            patchWindow(windowId, {
                messages: humanDM
                    ? [...w.messages, userMsg]
                    : [...w.messages, userMsg, assistantMsg],
                ...(humanDM ? {} : { sending: true }),
                hint: null,
                // Reset content-type chip after every send. Per-turn ephemeral
                // so a follow-up message ("explain that code") doesn't carry
                // the prior turn's content-type bias forward.
                nextMessageContentType: 'text',
            });
            try {
                // --- ensureSession ---
                let sid = get().windows[windowId]?.sessionId ?? null;
                if (!sid) {
                    // New clean shape (workbench C.5.1): pass `recipient` as a
                    // discriminated object. BFF derives recipientKey + populates
                    // both fields on the session droplet.
                    //
                    // recipientForWire() projects the UI Recipient down to its
                    // schema-pure shape -- strips name/email/avatarEmoji that
                    // the server's strict validator would reject (per F.9
                    // strict-contracts: no fail-forward, no silent normalization).
                    const body = {
                        title: 'New conversation',
                        recipient: recipientForWire(w.recipient),
                    };
                    const r = await api.post(`/p/${projectId}/chat/sessions`, body);
                    sid = r.session.sessionId;
                    patchWindow(windowId, {
                        sessionId: sid,
                        sessionTitle: r.session.title ?? null,
                    });
                }
                // --- folder context (per-window) ---
                const cur = get().windows[windowId];
                const cfMeta = cur?.currentFolderMeta ?? null;
                const prevSent = cur?.lastSentFolderId ?? null;
                const folderContext = cfMeta
                    ? {
                        currentFolderId: cfMeta.id,
                        currentFolderName: cfMeta.name,
                        currentFolderPath: cfMeta.path,
                        ...(prevSent && prevSent !== cfMeta.id ? { previousFolderId: prevSent } : {}),
                    }
                    : undefined;
                if (cfMeta)
                    patchWindow(windowId, { lastSentFolderId: cfMeta.id });
                // --- stream ---
                // Hint sent SEPARATELY from text -- the BFF composes hint+text
                // for the LLM but persists only `content` (the typed text)
                // into the message droplet. No hint-bleed in replays.
                //
                // Register a per-window AbortController so the Stop button on
                // the textarea (humanDM=false case) can cancel the in-flight
                // LLM stream. The map entry is removed in the finally block
                // below regardless of success/error/abort.
                const abortController = new AbortController();
                inFlightControllers.set(windowId, abortController);
                await streamAgent(projectId, sid, trimmed, folderContext, (ev) => {
                    // Reduce the event into THIS window's messages.
                    applyEvent(ev, setMessagesFor(windowId));
                    // Side-effects fan out to project-global handlers.
                    if (ev.type === 'side-effect' && ev.sideEffect) {
                        dispatchSideEffect(ev.sideEffect);
                    }
                    // Auto-titler -- update window's title in place.
                    if (ev.type === 'session-title' && typeof ev.title === 'string') {
                        patchWindow(windowId, { sessionTitle: ev.title });
                    }
                    // ID-SWAP on server confirmation. Replace the optimistic
                    // local id (`u-<ts>` for user, `a-<ts>` for assistant)
                    // with the real server messageId so cursor tracking,
                    // scroll, retry, edit, reaction operations all see the
                    // canonical UUIDv7 id.
                    //
                    // Rationale -- the cursor invariant (see chat.ts comment
                    // block "Compute the two cursors that the chat UI tracks
                    // independently"): a cursor on the wire is ALWAYS the
                    // exact UUIDv7 scope key issued by the server. The
                    // optimistic ids `u-<ts>` / `a-<ts>` are local-only and
                    // MUST be replaced with real ids before any code path
                    // can read them out and pass them as a cursor.
                    //
                    // Today the cursor-advance paths (loadHistory seed,
                    // pollNew receipt) both source from server-confirmed ids
                    // so the immediate risk is contained. But the swap MUST
                    // happen anyway: a future feature reading `messages[i].id`
                    // for ANY purpose (edit, reaction, delete, citation
                    // anchor) needs the canonical id. Swap-on-confirm makes
                    // the local store agree with S3 byte-for-byte from the
                    // moment the server confirms, eliminating the class of
                    // bug where optimistic ids leak into wire requests.
                    if ((ev.type === 'user-saved' || ev.type === 'assistant-saved')
                        && typeof ev.messageId === 'string') {
                        const realId = ev.messageId;
                        const optimisticId = ev.type === 'user-saved'
                            ? optimisticUserId
                            : assistantMsg.id;
                        set((s) => {
                            const cur = s.windows[windowId];
                            if (!cur)
                                return s;
                            const swapped = cur.messages.map((m) => m.id === optimisticId ? { ...m, id: realId } : m);
                            return {
                                windows: {
                                    ...s.windows,
                                    [windowId]: { ...cur, messages: swapped },
                                },
                            };
                        });
                    }
                }, hiddenHint || undefined, sendContentType !== 'text' ? sendContentType : undefined, parentMessageId, abortController.signal);
            }
            catch (err) {
                // User-initiated Stop is clean termination, not an error.
                // The fetch throws DOMException("AbortError") when the
                // controller aborts; surface no error toast, no `state:
                // error` flag on the assistant placeholder.
                const isAbort = err instanceof DOMException && err.name === 'AbortError';
                if (isAbort) {
                    patchWindow(windowId, { sending: false });
                    inFlightControllers.delete(windowId);
                    return;
                }
                const msg = err instanceof Error ? err.message : 'unknown error';
                // Error landed -- mark assistant placeholder as errored.
                // Human-DMs may not have one; safe-guard via state check.
                set((s) => {
                    const cur = s.windows[windowId];
                    if (!cur)
                        return s;
                    const next = cur.messages.map((m) => m.id === assistantMsg.id ? { ...m, state: 'error', error: msg } : m);
                    return {
                        windows: { ...s.windows, [windowId]: { ...cur, messages: next, sending: false } },
                    };
                });
                inFlightControllers.delete(windowId);
                return;
            }
            inFlightControllers.delete(windowId);
            // Cursor advance is NOT done here. forwardCursor is a "I have
            // SEEN up to here" read receipt, not a "I have SENT up to here"
            // write log. It advances in exactly one place: pollNew's
            // append-new-messages path, when a server-confirmed message
            // arrives via the asc-poll delivery channel.
            //
            // Industry standard (Slack last_read, Teams readWatermark,
            // Discord last_message_id): sending your own message does not
            // advance the read cursor. The next asc-poll naturally includes
            // the user's just-sent message (post-id-swap with the real
            // server id), pollNew dedupes against the locally-rendered
            // copy AND advances the cursor at that point.
            //
            // The prior implementation advanced cursor here using
            // messages[length-1].id which, before the id-swap fix above,
            // captured the optimistic `u-<ts>` id and leaked it into the
            // next asc-poll request as ?cursor=u-1779597310794 -- a value
            // the server cannot resolve to any S3 scope-key, returning
            // empty deltas forever for that session.
            patchWindow(windowId, { sending: false });
        },
        /**
         * Stop an in-flight LLM stream. See ChatStore.stop comment for
         * the rationale. No-op when the window has nothing in-flight.
         *
         * The user-visible effect: the assistant placeholder stops
         * receiving streaming text, the send button returns from Stop
         * to Send, the user can compose immediately. Any assistant
         * content already streamed stays visible -- partial content
         * is better than no content. The BFF, upon detecting the
         * connection close, will save whatever the assistant has
         * produced so far as a normal assistant droplet (the BFF
         * already does this via its own try/finally on the LLM stream).
         */
        stop: (windowId) => {
            const ctrl = inFlightControllers.get(windowId);
            if (!ctrl)
                return;
            ctrl.abort();
            inFlightControllers.delete(windowId);
            // sending flag clears in the AbortError catch path; no
            // need to touch it here.
        },
        /**
         * Load the message history for a window's session. Resolves
         * sessionTitle from the session droplet too. Recipient is set at
         * openWindow time (from the caller's knowledge of who the session
         * is with), so loadHistory doesn't need to re-resolve it.
         *
         * SAFETY: skip if the window is currently sending. The local
         * optimistic state (user message + streaming assistant placeholder)
         * is authoritative until the SSE stream finishes; a poll-driven
         * refetch mid-stream would clobber the placeholder.
         */
        loadHistory: async (windowId) => {
            const projectId = get().projectId;
            if (!projectId)
                return;
            const w = get().windows[windowId];
            if (!w || !w.sessionId)
                return;
            if (w.sending)
                return;
            try {
                // Fetch viewer identity alongside session + messages so we can
                // re-resolve the window's recipient from the VIEWER's perspective
                // (workbench symmetric-DM contract from May 3 commit b325cba,
                // restored here after the lightning-port wave lost the logic
                // that used to live in useAgent::loadHistory). Without this swap
                // the user on the recipient seat sees their own avatar/name in
                // the header chip and the conversation looks asymmetric.
                //
                // viewer fetch is best-effort -- failures fall through and leave
                // the window's pre-existing recipient unchanged (which is the
                // correct value for the session owner's seat anyway, since
                // openWindow stored it from the user's POV when they created
                // the session).
                const [msgs, sess, viewer] = await Promise.all([
                    api.get(`/p/${projectId}/chat/sessions/${w.sessionId}/messages`),
                    api.get(`/p/${projectId}/chat/sessions/${w.sessionId}`).catch(() => ({
                        session: {},
                    })),
                    api
                        .get('/auth/me')
                        .catch(() => null),
                ]);
                const ui = msgs.messages.map((m) => ({
                    id: m.messageId,
                    role: m.role === 'tool' ? 'assistant' : m.role,
                    authorId: m.authorId ?? m.userId,
                    content: m.content,
                    thinking: (m.thinking ?? []).map((t, i) => ({
                        iteration: i + 1,
                        toolName: t.tool,
                        args: t.args,
                        state: t.ok ? 'ok' : 'error',
                        preview: t.resultPreview,
                    })),
                    ...(m.citations !== undefined ? { citations: m.citations } : {}),
                    ...(m.generatedDocuments !== undefined && m.generatedDocuments.length > 0
                        ? { generatedDocuments: m.generatedDocuments }
                        : {}),
                    createdAt: m.createdAt,
                    state: 'complete',
                }));
                // Viewer-perspective recipient resolution. Only re-derives the
                // recipient when the server returned the discriminated field
                // AND we have a viewer userId AND the recipient is human-DM
                // (the only case where viewer-vs-owner matters).
                //
                // - coworker / group / fdn: server-side recipient is already
                //   correct for any viewer (no per-viewer swap needed). Leave
                //   the window's existing recipient -- it was set from the
                //   creator's POV but the values are viewer-independent.
                // - user: when viewer === owner, recipient.userId IS the other
                //   party; when viewer === recipient, the "other party" is
                //   the owner. We swap so the chip + header always show the
                //   counterpart, never the viewer's own identity.
                const srvRecipient = sess.session?.recipient;
                const ownerId = sess.session?.userId;
                let derivedRecipient;
                if (srvRecipient?.kind === 'user' && viewer?.userId && ownerId) {
                    const recipientUserId = srvRecipient.userId;
                    const otherId = ownerId === viewer.userId
                        ? recipientUserId
                        : recipientUserId === viewer.userId
                            ? ownerId
                            : recipientUserId;
                    // Keep the existing window.recipient's name/email if it was
                    // already populated for `otherId`. Re-fetching the member
                    // here would be a network round-trip per loadHistory; the
                    // ConversationsPanel resume path + the per-window state both
                    // already resolve the name when needed. If we don't have a
                    // cached name we use the otherId as a stub -- the header
                    // will fall back to it and re-render once any other path
                    // populates the member cache.
                    const prev = w.recipient;
                    const prevName = prev.kind === 'user' && prev.userId === otherId
                        ? prev.name
                        : undefined;
                    const prevEmail = prev.kind === 'user' && prev.userId === otherId
                        ? prev.email
                        : undefined;
                    derivedRecipient = {
                        kind: 'user',
                        userId: otherId,
                        name: prevName ?? otherId,
                        ...(prevEmail ? { email: prevEmail } : {}),
                    };
                }
                // Seed forwardCursor for the asc-poll loop. The wire format
                // returns messages chronologically (oldest first); the newest
                // message is at the END of the array. After this loadHistory
                // settles, polling can call pollNew(windowId) to fetch deltas
                // via mode=asc&cursor=forwardCursor instead of re-fetching the
                // whole history on every update.
                const newestMessageId = ui.length > 0 ? ui[ui.length - 1].id : null;
                patchWindow(windowId, {
                    messages: ui,
                    sessionTitle: sess.session?.title ?? null,
                    ...(sess.session?.userId ? { sessionOwnerId: sess.session.userId } : {}),
                    // sessionParticipants: present if returned (null OR array
                    // are both meaningful values; undefined would mean
                    // "loadHistory bailed before reaching the session GET").
                    sessionParticipants: sess.session?.participants ?? null,
                    ...(derivedRecipient ? { recipient: derivedRecipient } : {}),
                    forwardCursor: newestMessageId,
                });
            }
            catch (err) {
                // Best-effort -- leave the window's existing state if the
                // history fetch fails (network blip, 403, etc).
                // eslint-disable-next-line no-console
                console.warn('loadHistory failed:', err);
            }
        },
        pollNew: async (windowId) => {
            const projectId = get().projectId;
            if (!projectId)
                return;
            const w = get().windows[windowId];
            if (!w || !w.sessionId)
                return;
            // No cursor yet -- defer to loadHistory which seeds it. This is
            // the "first visit" path: don't asc-poll from nothing.
            if (!w.forwardCursor)
                return;
            const cursor = w.forwardCursor;
            try {
                const res = await api.get(`/p/${projectId}/chat/sessions/${w.sessionId}/messages?mode=asc&cursor=${encodeURIComponent(cursor)}`);
                const newMsgs = res.messages ?? [];
                if (newMsgs.length === 0)
                    return; // nothing newer; common case
                // Re-read window state -- it may have changed during the await.
                // The asc-poll only APPENDS; we never replace existing messages.
                //
                // Dedup must handle three cases:
                //   (1) Already-swapped: existing message has the real server
                //       id (post-swap). Standard id-set check.
                //   (2) Pre-swap race: poll returned BEFORE the SSE
                //       user-saved / assistant-saved event landed, so the
                //       optimistic message still has its local id
                //       (`u-<ts>` or `a-<ts>`). Match on
                //       (authorId, content, role) tuple instead -- two
                //       chronologically-adjacent messages from the same
                //       author with the same content are astronomically
                //       unlikely to be distinct. When matched, the optimistic
                //       message gets its id swapped to the server's id in
                //       place (same as the SSE swap would do), AND the polled
                //       message is dropped from toAppend.
                //   (3) Truly new: not matched by either rule -> append.
                //
                // The pre-swap race is real because the SSE round-trip and
                // the poll-tick are independent; for human-only DMs the
                // window is ~200ms, for LLM sessions (assistant-saved fires
                // AFTER LLM completes) the window can be 30s+. Either way,
                // missing this dedup would render the same message twice
                // (once optimistic, once polled-in).
                const cur = get().windows[windowId];
                if (!cur)
                    return;
                const existingIds = new Set(cur.messages.map((m) => m.id));
                const tupleKey = (authorId, role, content) => `${authorId ?? ''}\u0001${role}\u0001${content}`;
                const optimisticByTuple = new Map();
                for (const m of cur.messages) {
                    if (m.id.startsWith('u-') || m.id.startsWith('a-')) {
                        optimisticByTuple.set(tupleKey(m.authorId, m.role, m.content), m.id);
                    }
                }
                const toAppend = [];
                const swaps = [];
                for (const m of newMsgs) {
                    if (existingIds.has(m.messageId))
                        continue;
                    const polledAuthor = m.authorId ?? m.userId;
                    const polledRole = m.role === 'tool' ? 'assistant' : m.role;
                    const matchedOptimistic = optimisticByTuple.get(tupleKey(polledAuthor, polledRole, m.content));
                    if (matchedOptimistic) {
                        swaps.push({ optimisticId: matchedOptimistic, realId: m.messageId });
                        // Don't re-add to optimisticByTuple -- each optimistic
                        // message swaps to exactly one real id; a hypothetical
                        // duplicate would be a real new message.
                        optimisticByTuple.delete(tupleKey(polledAuthor, polledRole, m.content));
                        continue;
                    }
                    toAppend.push({
                        id: m.messageId,
                        role: polledRole,
                        authorId: polledAuthor,
                        content: m.content,
                        thinking: (m.thinking ?? []).map((t, i) => ({
                            iteration: i + 1,
                            toolName: t.tool,
                            args: t.args,
                            state: t.ok ? 'ok' : 'error',
                            preview: t.resultPreview,
                        })),
                        ...(m.citations !== undefined ? { citations: m.citations } : {}),
                        ...(m.generatedDocuments !== undefined && m.generatedDocuments.length > 0
                            ? { generatedDocuments: m.generatedDocuments }
                            : {}),
                        createdAt: m.createdAt,
                        state: 'complete',
                    });
                }
                // Apply any pre-swap-race id-swaps. Same shape as the
                // SSE-handler swap above, just driven by poll-receipt
                // instead of SSE-receipt.
                if (swaps.length > 0) {
                    set((s) => {
                        const cur2 = s.windows[windowId];
                        if (!cur2)
                            return s;
                        const swapMap = new Map(swaps.map((sw) => [sw.optimisticId, sw.realId]));
                        const swapped = cur2.messages.map((m) => {
                            const realId = swapMap.get(m.id);
                            return realId ? { ...m, id: realId } : m;
                        });
                        return {
                            windows: {
                                ...s.windows,
                                [windowId]: { ...cur2, messages: swapped },
                            },
                        };
                    });
                }
                if (toAppend.length === 0 && swaps.length === 0)
                    return;
                if (toAppend.length === 0) {
                    // All polled messages were dedup'd via swap; still need to
                    // advance forwardCursor so the next poll skips past them.
                    const lastPolled = newMsgs[newMsgs.length - 1].messageId;
                    patchWindow(windowId, { forwardCursor: lastPolled });
                    return;
                }
                // forwardCursor advances to the newest message we actually
                // appended (could be smaller than res.forwardCursor if dedup
                // dropped some; that's the right cursor for the next poll).
                const nextCursor = res.forwardCursor && newMsgs.length > 0
                    ? newMsgs[newMsgs.length - 1].messageId
                    : cur.forwardCursor;
                patchWindow(windowId, {
                    messages: [...cur.messages, ...toAppend],
                    forwardCursor: nextCursor,
                });
            }
            catch (err) {
                // Best-effort -- next poll tick will retry.
                // eslint-disable-next-line no-console
                console.warn('pollNew failed:', err);
            }
        },
        /**
         * Optimistic title rename. Reverts on failure.
         */
        renameWindow: async (windowId, title) => {
            const projectId = get().projectId;
            if (!projectId)
                return;
            const w = get().windows[windowId];
            if (!w || !w.sessionId)
                return;
            const trimmed = title.trim();
            if (!trimmed)
                return;
            const prevTitle = w.sessionTitle;
            patchWindow(windowId, { sessionTitle: trimmed });
            try {
                await api.patch(`/p/${projectId}/chat/sessions/${w.sessionId}/title`, { title: trimmed });
            }
            catch (err) {
                patchWindow(windowId, { sessionTitle: prevTitle });
                throw err;
            }
        },
        setSessionLock: async (windowId, locked) => {
            const projectId = get().projectId;
            if (!projectId)
                return;
            const w = get().windows[windowId];
            if (!w || !w.sessionId)
                return;
            // Optimistic update so the icon flips immediately. We don't know
            // the exact participants array the server will produce on lock
            // (it consults the coworker's current participants), but a non-
            // null sentinel array is enough to drive the UI's locked
            // indicator. The server response replaces it with the real
            // value.
            const prevParticipants = w.sessionParticipants;
            patchWindow(windowId, {
                sessionParticipants: locked ? [] : null,
            });
            try {
                const r = await api.patch(`/p/${projectId}/chat/sessions/${w.sessionId}/lock`, { locked });
                patchWindow(windowId, {
                    sessionParticipants: r.session?.participants ?? null,
                });
            }
            catch (err) {
                // Revert on failure (403 if non-owner, etc).
                patchWindow(windowId, { sessionParticipants: prevParticipants });
                // eslint-disable-next-line no-console
                console.warn('[setSessionLock] failed:', err);
                throw err;
            }
        },
        /**
         * Called by the polling layer when it detects new server-side
         * activity for a session. Routes to any windows tracking that
         * sessionId:
         *   - if minimized: bump inlineUnread (the pill renders the
         *     count). DND-aware: the pill bumps regardless of DND, but
         *     auto-restore (un-minimize) only happens when DND is OFF.
         *   - if visible: refetch history so the user sees the new
         *     content immediately.
         *
         * No-op for sessions that no window currently tracks -- the
         * dock-level unread badge handles that case (the user can
         * click the avatar / open the conversations panel to surface
         * the new message manually).
         */
        notifySessionAdvanced: (sessionId) => {
            const ws = get().windows;
            // Read DND lazily inside this dispatch so the user's DND
            // toggle takes effect immediately without re-binding any
            // listeners.
            let dndActive = false;
            try {
                dndActive = localStorage.getItem('fdn:dnd') === '1';
            }
            catch {
                // ignore
            }
            for (const id of Object.keys(ws)) {
                const w = ws[id];
                if (w.sessionId !== sessionId)
                    continue;
                // CRITICAL: never refetch history for a window that's currently
                // sending. The window's local optimistic state (user message +
                // streaming assistant placeholder) is authoritative until the
                // SSE stream finishes. A poll-driven loadHistory mid-stream
                // would clobber the placeholder + cause the user message to
                // disappear (the server has the user droplet but not yet the
                // assistant droplet, so the chain replay returns just
                // [userMsg-from-server], blowing away the local UI state).
                // The own-stream's `final` event will land any new content for
                // this window; once `sending: false` lands, future polls catch
                // up cleanly.
                if (w.sending)
                    continue;
                if (w.pose.minimized) {
                    patchWindow(id, { inlineUnread: w.inlineUnread + 1 });
                    // Auto-restore the window when DND is off so the user
                    // sees the new message immediately. Restoring also
                    // clears inlineUnread via minimizeWindow's logic.
                    if (!dndActive) {
                        const nextPose = {
                            ...w.pose,
                            minimized: false,
                            focusedAt: Date.now(),
                        };
                        savePose(id, nextPose);
                        patchWindow(id, { pose: nextPose, inlineUnread: 0 });
                        // Restored from minimized: use full loadHistory to ensure
                        // the restored window shows accumulated state (this is the
                        // first time the window catches up after potentially many
                        // missed messages).
                        void get().loadHistory(id);
                    }
                }
                else if (w.forwardCursor) {
                    // Already visible AND has a forwardCursor: use asc-poll to
                    // fetch only new arrivals (cheaper than refetching the
                    // whole chain, doesn't clobber any in-flight optimistic
                    // state). See RAINDB_OVERVIEW.md "asc/desc + cursor pattern."
                    void get().pollNew(id);
                }
                else {
                    // Already visible but no cursor yet (loadHistory hasn't
                    // settled). Bootstrap via loadHistory; the next poll tick
                    // will have a cursor and go through pollNew.
                    void get().loadHistory(id);
                }
            }
        },
    };
});
// ---------------------------------------------------------------------------
// Selectors -- shorthand hooks for common subscriptions
// ---------------------------------------------------------------------------
export function useChatWindow(windowId) {
    return useChatStore((s) => s.windows[windowId]);
}
export function useChatWindowIds() {
    return useChatStore((s) => s.windowOrder);
}
/**
 * Returns the index of THIS window among currently-minimized windows
 * in windowOrder, or -1 if the window isn't minimized. Drives the
 * pill-strip layout: pill 0 is the rightmost, pill 1 to its left, etc.
 *
 * The selector returns just the integer (cheap to compare) so a
 * window's pill only re-renders when its OWN minimization-index
 * changes (not when other unrelated windows update).
 */
export function useMinimizedIndex(windowId) {
    return useChatStore((s) => {
        const minimized = s.windowOrder.filter((id) => s.windows[id]?.pose.minimized);
        return minimized.indexOf(windowId);
    });
}
export function useProjectId() {
    return useChatStore((s) => s.projectId);
}
